import { formatDateTime } from "@/utils/formatDateTime";
import { useNewApiFetcher } from "@app/hooks/fetcher";
import { Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import dayjs from "dayjs";

interface HistoryItem {
  id: number;
  userId: number;
  symbol: string;
  fromWallet: number | null;
  toWallet: number | null;
  amount: string;
  type: string;
  transactionType: string;
  detail: string | null;
  info: string | null;
  sentAt: string;
  adminMemo: string | null;
  beforeAmount: string | null;
  afterAmount: string | null;
  walletType: number | null;
}

interface AssetOverallHistoryTableProps {
  tradingType: string;
  walletType: string;
  transactionType: string;
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
}

const AssetOverallHistoryTable: React.FC<AssetOverallHistoryTableProps> = ({
  tradingType,
  walletType,
  transactionType,
  dateRange,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Build API URL with filters
  const apiUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.append("tradingType", tradingType);
    params.append("page", page.toString());
    params.append("maxData", pageSize.toString());

    if (walletType !== "all") {
      params.append("walletType", walletType);
    }

    if (transactionType !== "all") {
      params.append("transactionTypes", transactionType);
    }

    if (dateRange[0] && dateRange[1]) {
      params.append("startDate", dateRange[0].format("YYYY-MM-DD"));
      params.append("endDate", dateRange[1].format("YYYY-MM-DD"));
    }

    return `/wallet/history?${params.toString()}`;
  }, [tradingType, walletType, transactionType, dateRange, page]);

  const { data, isLoading } = useNewApiFetcher(apiUrl, true, {
    revalidateOnFocus: true,
  });

  const getWalletTypeConfig = (type: number | null) => {
    switch (type) {
      case 0:
        return { color: "#B8860B", label: t("spotTag") }; // 골드 - 현물
      case 1:
        return { color: "#6C5CE7", label: t("futuresTag") }; // 인디고 - 선물
      default:
        return { color: "#888", label: "-" };
    }
  };

  const getTransactionTypeConfig = (transactionType: string) => {
    const configs: Record<string, { color: string; label: string }> = {
      DEPOSIT: { color: "#2ebd85", label: t("deposit") },
      WITHDRAWAL: { color: "#f6465d", label: t("withdrawal") },
      ADMIN_DEPOSIT: { color: "#16a085", label: t("adminDeposit") },
      TRANSFER_IN: { color: "#4a90e2", label: t("transferIn") },
      TRANSFER_OUT: { color: "#9b59b6", label: t("transferOut") },
      TRADE_FEE: { color: "#e67e22", label: t("tradeFee") },
      REALIZED_PNL: { color: "#27ae60", label: t("realizedPnl") },
      COMMISSION: { color: "#3498db", label: t("referralCommission") },
      REWARD: { color: "#f1c40f", label: t("reward") },
      FUNDING_FEE_PAID: { color: "#e74c3c", label: t("fundingFeeMinus") },
      FUNDING_FEE_RECEIVED: { color: "#2ecc71", label: t("fundingFeePlus") },
      LIQUIDATION: { color: "#c0392b", label: t("forcedLiquidation") },
      LOCK: { color: "#95a5a6", label: t("lock") },
      UNLOCK: { color: "#7f8c8d", label: t("unlock") },
      ADJUSTMENT: { color: "#8e44ad", label: t("adjustment") },
    };

    return configs[transactionType] || { color: "#888", label: transactionType };
  };

  const columns: ColumnsType<HistoryItem> = [
    {
      title: t("date"),
      dataIndex: "sentAt",
      key: "sentAt",
      align: "center",
      width: 160,
      render: (text: string) => formatDateTime(text),
    },
    {
      title: t("tradingType"),
      key: "tradingType",
      align: "center",
      width: 100,
      render: () => {
        const labels: Record<string, string> = {
          real: t("futuresTrading"),
          bot: t("botTrading"),
          copy: t("copyTrading"),
        };
        return <span>{labels[tradingType] || tradingType}</span>;
      },
    },
    {
      title: t("walletType"),
      dataIndex: "walletType",
      key: "walletType",
      align: "center",
      width: 100,
      render: (walletType: number | null) => {
        const config = getWalletTypeConfig(walletType);
        return (
          <Tag
            style={{
              backgroundColor: config.color,
              color: "white",
              padding: "4px 12px",
              borderRadius: "4px",
              border: "none",
              fontWeight: 600,
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: t("symbol"),
      dataIndex: "symbol",
      key: "symbol",
      align: "center",
      width: 80,
    },
    {
      title: t("transactionType"),
      dataIndex: "transactionType",
      key: "transactionType",
      align: "center",
      width: 140,
      render: (type: string) => {
        const config = getTransactionTypeConfig(type);
        return (
          <Tag
            style={{
              backgroundColor: config.color,
              color: "white",
              padding: "4px 12px",
              borderRadius: "4px",
              border: "none",
              fontWeight: 500,
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: t("amount"),
      dataIndex: "amount",
      key: "amount",
      align: "center",
      width: 120,
      render: (amount: string, record: HistoryItem) => {
        const isPositive = record.type === "D";
        return (
          <AmountText positive={isPositive}>
            {isPositive ? "+" : "-"}
            {Number(amount).toFixed(4)}
          </AmountText>
        );
      },
    },
    {
      title: t("beforeBalance"),
      dataIndex: "beforeAmount",
      key: "beforeAmount",
      align: "center",
      width: 120,
      render: (amount: string | null) =>
        amount ? Number(amount).toFixed(4) : "-",
    },
    {
      title: t("afterBalance"),
      dataIndex: "afterAmount",
      key: "afterAmount",
      align: "center",
      width: 120,
      render: (amount: string | null) =>
        amount ? Number(amount).toFixed(4) : "-",
    },
    {
      title: t("info"),
      dataIndex: "info",
      key: "info",
      align: "center",
      width: 200,
      render: (info: string | null) => (
        <InfoText>{info || "-"}</InfoText>
      ),
    },
  ];

  return (
    <HistoryTableContainer>
      <TableHeader>
        <h3>{t("transactionHistory")}</h3>
        <SubText>
          {t("totalRecords")}: {data?.result?.totalCount || 0}
        </SubText>
      </TableHeader>

      <Table<HistoryItem>
        columns={columns}
        dataSource={data?.result?.list || []}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data?.result?.totalCount || 0,
          onChange: (newPage) => setPage(newPage),
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} / ${total} ${t("items")}`,
        }}
        rowKey={(record) => record.id}
        scroll={{ x: "max-content" }}
        loading={isLoading}
        locale={{
          emptyText: t("noTransactionHistory"),
        }}
      />
    </HistoryTableContainer>
  );
};

const HistoryTableContainer = styled.div`
  width: 100%;
  margin-top: 30px;
  padding: 20px;
  background: ${({ theme }) => theme.itemBg};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.COLORS.PrimaryBorder};
`;

const TableHeader = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.COLORS.textw};
  }
`;

const SubText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.secondaryTextColor || "#666"};
`;

const AmountText = styled.span<{ positive: boolean }>`
  font-weight: 600;
  color: ${(props) =>
    props.positive ? "rgb(46, 189, 133)" : "rgb(246, 70, 93)"};
`;

const InfoText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.secondaryTextColor || "#666"};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
`;

export default AssetOverallHistoryTable;
